import type { BillingTier } from "@/packages/domains/billing";

export const POLAR_PRODUCT_IDS = {
  essentials: import.meta.env.VITE_POLAR_PRODUCT_ESSENTIALS ?? "",
  professional: import.meta.env.VITE_POLAR_PRODUCT_PROFESSIONAL ?? "",
  unlimited: import.meta.env.VITE_POLAR_PRODUCT_UNLIMITED ?? "",
} as const;

export type BillingInterval = "monthly" | "yearly";

type PlanPricing = {
  monthly: { price: number; extraUserPrice: number };
  yearly: { price: number; extraUserPrice: number };
};

export type PaidTier = "essentials" | "professional" | "unlimited";

export const PRICING: Record<PaidTier, PlanPricing> = {
  essentials: {
    monthly: { price: 149, extraUserPrice: 50 },
    yearly: { price: 119, extraUserPrice: 45 },
  },
  professional: {
    monthly: { price: 349, extraUserPrice: 35 },
    yearly: { price: 279, extraUserPrice: 30 },
  },
  unlimited: {
    monthly: { price: 599, extraUserPrice: 25 },
    yearly: { price: 479, extraUserPrice: 20 },
  },
};

export const TIER_ORDER: Record<BillingTier, number> = {
  free: 0,
  essentials: 1,
  professional: 2,
  unlimited: 3,
};

export interface TierFeature {
  /** i18n key suffix under settings:billing.pricing.tierFeatures.* */
  labelKey: string;
  included: boolean;
}

/**
 * Per-tier feature lists matching audinero.de/preise exactly.
 * Included features first, excluded last — component renders in this order.
 */
export const TIER_FEATURES: Record<PaidTier, TierFeature[]> = {
  essentials: [
    // Included
    { labelKey: "audits3", included: true },
    { labelKey: "workspace1User1", included: true },
    { labelKey: "neroKiAssistent", included: true },
    { labelKey: "aiCredits50", included: true },
    { labelKey: "gapAnalysisBasic", included: true },
    { labelKey: "dashboardPdf", included: true },
    { labelKey: "emailSupport", included: true },
    // Excluded
    { labelKey: "fullGapAnalysis", included: false },
    { labelKey: "byoa", included: false },
    { labelKey: "prioritySupport", included: false },
    { labelKey: "predictiveCompliance", included: false },
    { labelKey: "aiReports", included: false },
    { labelKey: "gapAnalysisAutoFix", included: false },
    { labelKey: "customerManager", included: false },
  ],
  professional: [
    // Included (all from Essentials + own)
    { labelKey: "audits5", included: true },
    { labelKey: "workspace3User5", included: true },
    { labelKey: "neroKiAssistent", included: true },
    { labelKey: "aiCredits100", included: true },
    { labelKey: "fullGapAnalysis", included: true },
    { labelKey: "dashboardPdf", included: true },
    { labelKey: "byoa", included: true },
    { labelKey: "prioritySupport", included: true },
    // Excluded
    { labelKey: "predictiveCompliance", included: false },
    { labelKey: "aiReports", included: false },
    { labelKey: "gapAnalysisAutoFix", included: false },
    { labelKey: "customerManager", included: false },
  ],
  unlimited: [
    // All included
    { labelKey: "auditsUnlimited25Users", included: true },
    { labelKey: "workspacesUnlimited", included: true },
    { labelKey: "neroKiAssistent", included: true },
    { labelKey: "aiCreditsUnlimited", included: true },
    { labelKey: "gapAnalysisAutoFix", included: true },
    { labelKey: "dashboardPdf", included: true },
    { labelKey: "byoa", included: true },
    { labelKey: "predictiveCompliance", included: true },
    { labelKey: "aiReports", included: true },
    { labelKey: "customerManager", included: true },
  ],
};
